import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Activity, Building2, Scale, MailCheck, NotebookPen, HeartPulse, Cloud, Contact, Globe, LifeBuoy, Plus, Settings, Menu, ShieldCheck, Wallet } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAdmin } from "@/components/auth/AdminGate";
import { Logo } from "./Logo";
import { ReminderBell } from "./ReminderBell";
import { OpsAlertBadge } from "@/components/ops/OpsAlertBadge";

const NAV = [
  { to: "/", label: "Siti web", icon: Globe, end: true, id: "projects" },
  { to: "/new", label: "Nuovo sito", icon: Plus, id: "new-project" },
  { to: "/clients", label: "Clienti", icon: Building2, id: "clients" },
  { to: "/support", label: "Richieste clienti", icon: LifeBuoy, id: "support" },
  { to: "/automation-diagnostics", label: "Diagnostica automazioni", icon: Activity, id: "automation-diagnostics" },
  { to: "/crm", label: "CRM", icon: Contact, id: "crm", crm: true },
  { to: "/hosting", label: "Hosting dei siti", icon: Cloud, id: "hosting" },
  { to: "/costs", label: "Costi e utilizzo", icon: Wallet, id: "costs" },
  { to: "/wallet-reconciliation", label: "Riconciliazione crediti", icon: Scale, id: "wallet-reconciliation" },
  { to: "/email-activation", label: "Attivazione email", icon: MailCheck, id: "email-activation" },
  { to: "/pilot-diary", label: "Diario pilota", icon: NotebookPen, id: "pilot-diary" },
  { to: "/customer-health", label: "Salute clienti", icon: HeartPulse, id: "customer-health" },
  { to: "/privacy-requests", label: "Richieste privacy", icon: ShieldCheck, id: "privacy-requests" },
  { to: "/backup", label: "Backup e sicurezza", icon: ShieldCheck, id: "backup" },
  { to: "/settings", label: "Impostazioni", icon: Settings, id: "settings" },
];

function NavItems({ onNavigate }) {
  const { me } = useAdmin();
  const { pathname } = useLocation();
  const siteArea = /^\/(sites|projects)(\/|$)/.test(pathname);  // site pages and the editor belong to «Siti web»
  return (
    <nav className="flex flex-col gap-1">
      {NAV.filter((x) => !x.crm || !me?.pilot_instance).map(({ to, label, icon: Icon, end, id }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${id}`}
          className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive || (id === "projects" && siteArea) ? "bg-navy-800 text-white" : "text-slate-400 hover:text-white hover:bg-navy-800/60"}`}>
          <Icon size={17} strokeWidth={1.8} /> {label}{id === "automation-diagnostics" && <OpsAlertBadge />}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarBody({ onNavigate }) {
  const { me } = useAdmin();
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 px-5 pt-6 pb-6 [@media(max-height:760px)]:pt-4 [@media(max-height:760px)]:pb-3"><Logo dark /></div>
      {/* only the menu scrolls: logo and profile stay visible, the page behind does not scroll with it */}
      <div className="px-3 pb-3 flex-1 min-h-0 overflow-y-auto overscroll-contain [scrollbar-width:thin] [scrollbar-color:theme(colors.slate.600)_transparent]" data-testid="sidebar-nav-scroll">
        <NavItems onNavigate={onNavigate} /><div className="mt-1"><ReminderBell /></div>
      </div>
      <div className="shrink-0 px-5 py-4 border-t border-navy-800 flex items-center gap-3" data-testid="admin-profile">
        <UserButton />
        <div className="min-w-0">
          <p className="text-xs text-white truncate">{me?.email}</p>
          <p className="text-[11px] text-slate-500">Amministratore</p>
        </div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-20 w-64 h-[100dvh] bg-navy-900" data-testid="app-sidebar"><SidebarBody /></aside>
      <header className="lg:hidden sticky top-0 z-30 bg-navy-900 px-4 h-14 flex items-center justify-between">
        <Link to="/"><Logo dark /></Link>
        <button className="-mr-2 flex h-11 w-11 items-center justify-center rounded-md text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" onClick={() => setOpen(true)} aria-label="Menu" data-testid="mobile-menu-btn"><Menu size={20} /></button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 bg-navy-900 border-navy-800" closeLabel="Chiudi menu"
          closeClassName="right-2 top-2 h-11 w-11 rounded-full text-white opacity-100 ring-1 ring-white/25 data-[state=open]:bg-white/10 hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-sky-400 focus:ring-offset-navy-900 [&>svg]:h-5 [&>svg]:w-5">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SidebarBody onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <main>{children}</main>
    </div>
  );
}
